import React, { useState, useEffect } from 'react';
import { authApi } from '../utils/api';
import { AuthCard, AuthHeading, AuthError, AUTH_LINK } from './AuthLayout';
import { AlertTriangleIcon, CheckIcon, LoaderCircleIcon } from './icons/LucideIcons';

const ResetPassword = ({ token, onSuccess, onBackToLogin }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid reset link');
        setValidatingToken(false);
        return;
      }

      try {
        const response = await authApi.validateResetToken(token);
        if (response.valid) {
          setTokenValid(true);
        } else {
          setError('This reset link has expired or is invalid');
        }
      } catch (error) {
        setError('This reset link has expired or is invalid');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.resetPassword(token, formData.password);
      if (response.success) {
        setSuccess(true);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(response.message || 'Password reset failed');
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Password reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldClass = 'input-field h-ctl-lg px-3.5 text-base sm:text-base';

  if (validatingToken) {
    return (
      <AuthCard>
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-floral-100 text-floral-700">
          <LoaderCircleIcon size={20} className="animate-spin" />
        </div>
        <AuthHeading title="Checking your reset link" sub="One moment..." />
      </AuthCard>
    );
  }

  if (!tokenValid) {
    return (
      <AuthCard>
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-danger-bg text-danger-fg">
          <AlertTriangleIcon size={20} />
        </div>
        <AuthHeading
          title="This reset link doesn't work"
          sub={error || 'This password reset link is invalid or has expired.'}
        />
        <button type="button" onClick={onBackToLogin} className="btn-primary btn-lg w-full">
          Back to sign in
        </button>
      </AuthCard>
    );
  }

  if (success) {
    return (
      <AuthCard>
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-ok-bg text-ok-fg">
          <CheckIcon size={20} />
        </div>
        <AuthHeading
          title="Password changed"
          sub="You can now sign in with your new password."
        />
        <button type="button" onClick={onBackToLogin} className="btn-primary btn-lg w-full">
          Sign in
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard as="form" onSubmit={handleSubmit}>
      <AuthHeading title="Set a new password" />

      <div>
        <label htmlFor="password" className="input-label">New password</label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          className={fieldClass}
          value={formData.password}
          onChange={handleInputChange}
        />
        <p className="input-help">At least 6 characters, with an uppercase letter, a lowercase letter and a number.</p>
      </div>

      <div>
        <label htmlFor="confirmPassword" className="input-label">Confirm new password</label>
        <input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          className={fieldClass}
          value={formData.confirmPassword}
          onChange={handleInputChange}
        />
      </div>

      {error && <AuthError>{error}</AuthError>}

      <button type="submit" disabled={loading} className="btn-primary btn-lg mt-1 w-full">
        {loading ? (
          <>
            <LoaderCircleIcon size={18} className="animate-spin" />
            Resetting password...
          </>
        ) : (
          'Reset password'
        )}
      </button>

      <button
        type="button"
        onClick={onBackToLogin}
        className={`self-center text-[13px] ${AUTH_LINK}`}
      >
        Back to sign in
      </button>
    </AuthCard>
  );
};

export default ResetPassword;